import { type VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { ButtonOrLinkProps, ButtonOrLink } from '../button-or-link';

import type { JSX } from 'react';

export interface ButtonProps
    extends ButtonOrLinkProps,
        VariantProps<typeof ButtonStyles> {
    disabled?: boolean;
    disabledStyling?: boolean;
    isMainBrand?: boolean;
    type?: 'button' | 'submit' | 'reset' | undefined;
}

export const ButtonStyles = cva(
    'inline-block text-center font-medium focus rounded-full focus:rounded-full focus-within:rounded-full focus-visible:rounded-full transition select-none',
    {
        variants: {
            intent: {
                primary:
                    'text-buttonPrimary bg-buttonPrimary hover:bg-buttonPrimaryHover',
                secondary:
                    'text-buttonSecondary bg-buttonSecondary hover:bg-buttonSecondaryHover hover:text-buttonSecondaryHover',
                tertiary: 'text-buttonTertiary bg-buttonTertiary',
                quaternary:
                    'text-buttonQuaternary hover:text-buttonQuaternary border border-buttonQuaternary',
                quinary:
                    'text-white border border-white hover:border-transparent hover:text-buttonQuaternary hover:bg-buttonSecondaryHover',
                vip: 'bg-gradient-bt',
                mastercard:
                    'text-white bg-mastercard-brand-700 hover:bg-mastercard-accent',
            },
            size: {
                sm: 'px-4 py-1.5 text-sm',
                md: 'px-6 py-2',
                lg: 'px-8 py-3',
            },
            disabled: {
                true: 'opacity-50 pointer-events-none',
            },
            disabledStyling: {
                true: 'opacity-50',
            },
            fullWidth: {
                true: 'w-full',
                false: 'w-fit',
            },
        },
        defaultVariants: {
            intent: 'primary',
            size: 'md',
        },
    }
);

export const Button = ({
    intent,
    size,
    disabled,
    fullWidth,
    className,
    isMainBrand = false,
    testId = 'button',
    disabledStyling,
    type,
    ...props
}: ButtonProps): JSX.Element => {
    const getHoverClass = (): string => {
        if (intent === 'tertiary') {
            return isMainBrand
                ? 'hover:text-white hover:bg-ba-accent'
                : 'hover:text-buttonTertiaryHover hover:bg-buttonTertiaryHover';
        }
        if (intent === 'quaternary') {
            return isMainBrand
                ? 'hover:border-ba-accent'
                : 'hover:border-buttonQuaternaryHover';
        }
        return '';
    };

    return (
        <ButtonOrLink
            className={twMerge(
                ButtonStyles({
                    intent,
                    size,
                    disabled,
                    fullWidth,
                    className,
                    disabledStyling,
                }),
                getHoverClass()
            )}
            testId={testId}
            type={type}
            {...props}
        />
    );
};
